import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  CalendarDays, Clock, Users, Plus, ChevronLeft, ChevronRight,
  Trash2, Edit2, User, Coffee
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useStandort } from "../hooks/useStandort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Schichtplan = () => {
  const { activeStandortId, activeStandort } = useStandort();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schichten, setSchichten] = useState({});
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [statistik, setStatistik] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchicht, setEditingSchicht] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [schichtForm, setSchichtForm] = useState({
    mitarbeiter_id: "",
    von: "18:00",
    bis: "02:00",
    pause_minuten: "30",
    position: "",
    notiz: ""
  });

  // Wochentage
  const wochentage = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const wochentageFull = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

  useEffect(() => {
    fetchMitarbeiter();
  }, [activeStandortId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const kw = getKW(currentDate);
    fetchWochenSchichten(currentDate.getFullYear(), kw);
    fetchStatistik();
  }, [currentDate, activeStandortId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getKW = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const fetchMitarbeiter = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.get(`${API}/mitarbeiter`, { params, withCredentials: true });
      setMitarbeiter(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchWochenSchichten = async (jahr, kw) => {
    setLoading(true);
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.get(`${API}/schichtplan/woche/${jahr}/${kw}`, { params, withCredentials: true });
      setSchichten(res.data.schichten || {});
    } catch (error) {
      console.error("Error:", error);
      setSchichten({});
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistik = async () => {
    const monat = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const res = await axios.get(`${API}/schichtplan/statistik/${monat}`, { params, withCredentials: true });
      setStatistik(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const handleAddSchicht = (datum) => {
    setSelectedDate(datum);
    setEditingSchicht(null);
    setSchichtForm({
      mitarbeiter_id: "",
      von: "18:00",
      bis: "02:00",
      pause_minuten: "30",
      position: "",
      notiz: ""
    });
    setDialogOpen(true);
  };

  const handleEditSchicht = (schicht) => {
    setSelectedDate(schicht.datum);
    setEditingSchicht(schicht);
    setSchichtForm({
      mitarbeiter_id: schicht.mitarbeiter_id,
      von: schicht.von,
      bis: schicht.bis,
      pause_minuten: String(schicht.pause_minuten || 0),
      position: schicht.position || "",
      notiz: schicht.notiz || ""
    });
    setDialogOpen(true);
  };

  const handleSaveSchicht = async (e) => {
    e.preventDefault();
    
    if (!schichtForm.mitarbeiter_id) {
      toast.error("Bitte Mitarbeiter auswählen");
      return;
    }

    try {
      const payload = {
        datum: selectedDate,
        mitarbeiter_id: schichtForm.mitarbeiter_id,
        von: schichtForm.von,
        bis: schichtForm.bis,
        pause_minuten: parseInt(schichtForm.pause_minuten) || 0,
        position: schichtForm.position,
        notiz: schichtForm.notiz,
        standort_id: activeStandortId || null
      };

      if (editingSchicht) {
        await axios.put(`${API}/schichtplan/${editingSchicht.id}`, payload, { withCredentials: true });
        toast.success("Schicht aktualisiert");
      } else {
        await axios.post(`${API}/schichtplan`, payload, { withCredentials: true });
        toast.success("Schicht hinzugefügt");
      }

      setDialogOpen(false);
      const kw = getKW(currentDate);
      fetchWochenSchichten(currentDate.getFullYear(), kw);
      fetchStatistik();
    } catch (error) {
      toast.error("Fehler beim Speichern");
      console.error(error);
    }
  };

  const handleDeleteSchicht = async (schichtId) => {
    if (!window.confirm("Schicht wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/schichtplan/${schichtId}`, { withCredentials: true });
      toast.success("Schicht gelöscht");
      const kw = getKW(currentDate);
      fetchWochenSchichten(currentDate.getFullYear(), kw);
      fetchStatistik();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  // Woche berechnen
  const monday = getMonday(currentDate);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const kw = getKW(currentDate);

  return (
    <div data-testid="schichtplan-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Schichtplan</h1>
            <p className="page-subtitle">
              Mitarbeiter-Einsatzplanung
              {activeStandort && (
                <span className="ml-2 text-violet-400" data-testid="active-standort-badge">
                  · {activeStandort.name}
                </span>
              )}
            </p>
          </div>
        </div>
      </header>

      <div className="px-8 py-4 space-y-6">
        {/* Wochen-Navigation */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigateWeek(-1)}
              className="border-slate-200"
            >
              <ChevronLeft size={16} className="mr-1" /> Vorherige Woche
            </Button>
            
            <div className="text-center">
              <h2 className="font-display text-lg font-semibold text-slate-800">
                Kalenderwoche {kw}
              </h2>
              <p className="text-sm text-slate-500">
                {new Date(weekDates[0]).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} - {new Date(weekDates[6]).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>

            <Button 
              variant="outline" 
              onClick={() => navigateWeek(1)}
              className="border-slate-200"
            >
              Nächste Woche <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>

        {/* Wochen-Übersicht */}
        <div className="grid grid-cols-7 gap-2">
          {weekDates.map((datum, i) => {
            const tagesSchichten = schichten[datum] || [];
            const isToday = datum === new Date().toISOString().split('T')[0];
            const dayDate = new Date(datum);
            
            return (
              <div 
                key={datum} 
                className={`bg-white border rounded-xl overflow-hidden ${isToday ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}
              >
                {/* Tag-Header */}
                <div className={`p-2 text-center ${isToday ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white' : 'bg-slate-50'}`}>
                  <p className="font-semibold text-sm">{wochentage[i]}</p>
                  <p className={`text-xs ${isToday ? 'text-blue-100' : 'text-slate-500'}`}>
                    {dayDate.getDate()}.{dayDate.getMonth() + 1}.
                  </p>
                </div>

                {/* Schichten */}
                <div className="p-2 min-h-[200px] space-y-2">
                  {tagesSchichten.map((schicht) => (
                    <div 
                      key={schicht.id}
                      className="bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 rounded-lg p-2 text-xs group relative"
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <User size={12} className="text-blue-600" />
                        <span className="font-medium text-slate-800 truncate">{schicht.mitarbeiter_name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock size={10} />
                        <span>{schicht.von} - {schicht.bis}</span>
                      </div>
                      <div className="text-violet-600 font-medium">
                        {schicht.stunden_netto?.toFixed(1)}h
                      </div>
                      
                      {/* Actions */}
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button 
                          onClick={() => handleEditSchicht(schicht)}
                          className="p-1 bg-white rounded shadow hover:bg-blue-50"
                        >
                          <Edit2 size={10} className="text-blue-600" />
                        </button>
                        <button 
                          onClick={() => handleDeleteSchicht(schicht.id)}
                          className="p-1 bg-white rounded shadow hover:bg-red-50"
                        >
                          <Trash2 size={10} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add Button */}
                  <button
                    onClick={() => handleAddSchicht(datum)}
                    className="w-full p-2 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-1"
                  >
                    <Plus size={14} />
                    <span className="text-xs">Schicht</span>
                  </button>
                </div>

                {/* Tages-Summe */}
                {tagesSchichten.length > 0 && (
                  <div className="p-2 bg-slate-50 border-t border-slate-100 text-center">
                    <span className="text-xs text-slate-500">
                      {tagesSchichten.reduce((sum, s) => sum + (s.stunden_netto || 0), 0).toFixed(1)}h
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Monats-Statistik */}
        {statistik && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-display text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CalendarDays size={18} className="text-violet-500" />
              Monats-Statistik ({statistik.monat})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Gesamt Stunden</p>
                <p className="text-2xl font-bold text-blue-600">{statistik.gesamt_stunden?.toFixed(1)}h</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Personalkosten</p>
                <p className="text-2xl font-bold text-violet-600">{formatCurrency(statistik.gesamt_kosten)}</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Mitarbeiter</p>
                <p className="text-2xl font-bold text-slate-700">{statistik.mitarbeiter?.length || 0}</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Ø Stunden/MA</p>
                <p className="text-2xl font-bold text-slate-700">
                  {statistik.mitarbeiter?.length ? (statistik.gesamt_stunden / statistik.mitarbeiter.length).toFixed(1) : 0}h
                </p>
              </div>
            </div>

            {/* Mitarbeiter-Details */}
            {statistik.mitarbeiter?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-200">
                      <th className="p-2 font-semibold text-slate-600">Mitarbeiter</th>
                      <th className="p-2 font-semibold text-slate-600 text-right">Schichten</th>
                      <th className="p-2 font-semibold text-slate-600 text-right">Stunden</th>
                      <th className="p-2 font-semibold text-slate-600 text-right">Kosten (brutto)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistik.mitarbeiter.map((ma) => (
                      <tr key={ma.mitarbeiter_id} className="border-b border-slate-100">
                        <td className="p-2 font-medium">{ma.name}</td>
                        <td className="p-2 text-right text-slate-600">{ma.schichten_anzahl}</td>
                        <td className="p-2 text-right font-mono">{ma.stunden_gesamt}h</td>
                        <td className="p-2 text-right font-mono text-violet-600">{formatCurrency(ma.kosten_brutto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schicht Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingSchicht ? "Schicht bearbeiten" : "Neue Schicht"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {selectedDate && new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveSchicht} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Mitarbeiter *</Label>
              <Select
                value={schichtForm.mitarbeiter_id}
                onValueChange={(value) => setSchichtForm({...schichtForm, mitarbeiter_id: value})}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder="Mitarbeiter wählen" />
                </SelectTrigger>
                <SelectContent>
                  {mitarbeiter.map((ma) => (
                    <SelectItem key={ma.id} value={ma.id}>
                      {ma.name} ({ma.position || 'Keine Position'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Von</Label>
                <Input
                  type="time"
                  value={schichtForm.von}
                  onChange={(e) => setSchichtForm({...schichtForm, von: e.target.value})}
                  className="border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Bis</Label>
                <Input
                  type="time"
                  value={schichtForm.bis}
                  onChange={(e) => setSchichtForm({...schichtForm, bis: e.target.value})}
                  className="border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Pause (Minuten)</Label>
              <Input
                type="number"
                value={schichtForm.pause_minuten}
                onChange={(e) => setSchichtForm({...schichtForm, pause_minuten: e.target.value})}
                placeholder="30"
                className="border-slate-200"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Notiz</Label>
              <Input
                value={schichtForm.notiz}
                onChange={(e) => setSchichtForm({...schichtForm, notiz: e.target.value})}
                placeholder="Optional"
                className="border-slate-200"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-violet-500 text-white hover:from-blue-600 hover:to-violet-600"
              >
                {editingSchicht ? "Speichern" : "Hinzufügen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schichtplan;
